import { action, makeObservable, observable, runInAction } from 'mobx';

import { StockSubscriptionsRequests } from '~/api/StockSubscriptions';
import { company } from '~/company/Company';
import { orderStore, settingStore, userStore } from '~/stores';
import { isClientError } from '~/utils/catalogWarehouse';

import { Product } from '../CategoriesStore';
import type { BaseStore } from '../interfaces/BaseStore';
import { MetaStore } from '../shared/MetaStore';

export interface Pagination {
  total: number;
  page: number;
  size: number;
}

export interface StockSubscriptionSKUResponse {
  data: Map<number, string[]>;
}

export class NotifyMeStore implements BaseStore {
  private readonly _abortController: AbortController = new AbortController();

  readonly notifyMeCRUDMeta: MetaStore = new MetaStore();
  readonly notifyMeProductListMeta: MetaStore = new MetaStore();
  readonly notifyMeSKUListMeta: MetaStore = new MetaStore();

  notifyMeProductList: Map<number, Product> = new Map();
  notifyMeSKUMap: Record<number, string[]> = {};

  gotSKUS = false;
  /** Only the latest subscriptions list request may write the map: the auth and ETA reactions race with different warehouses. */
  private skuListRequestId = 0;

  readonly notifyMeProductListPagination: Pagination = {
    total: 0,
    page: 0,
    size: 20,
  };

  constructor() {
    makeObservable(this, {
      notifyMeProductList: observable,
      notifyMeSKUMap: observable,
      addNotifyMe: action.bound,
      deleteNotifyMe: action.bound,
    });
  }

  async addNotifyMe(addProductId: number, addSKU: string): Promise<boolean> {
    if (this.notifyMeCRUDMeta.isLoading) {
      return false;
    }

    this.notifyMeCRUDMeta.setLoading(true);

    try {
      const {
        data: { productId, sku },
      } = await StockSubscriptionsRequests.addSubscription(
        addProductId,
        addSKU,
        this.stockWarehouseCode(),
        !!orderStore.etaCalculation?.warehouse?.availability?.deliveryMechanics
          ?.ON_DEMAND?.availableNow,
        settingStore.settings.backorderLogic,
        {
          signal: this._abortController.signal,
        },
      );
      if (this.notifyMeSKUMap[productId]) {
        this.notifyMeSKUMap[productId] = [
          ...this.notifyMeSKUMap[productId],
          sku,
        ];
      } else {
        this.notifyMeSKUMap[productId] = [sku];
      }
      return true;
    } catch (error) {
      error && console.error(error);
      return false;
    } finally {
      this.notifyMeCRUDMeta.setLoading(false);
    }
  }

  async deleteNotifyMe(
    deleteProductId: number,
    deleteSKU: string,
  ): Promise<boolean> {
    if (this.notifyMeCRUDMeta.isLoading) {
      return false;
    }

    this.notifyMeCRUDMeta.setLoading(true);

    try {
      await StockSubscriptionsRequests.deleteSubscription(
        deleteProductId,
        deleteSKU,
        this.stockWarehouseCode(),
        {
          signal: this._abortController.signal,
        },
      );

      if (this.notifyMeSKUMap[deleteProductId]?.length === 1) {
        delete this.notifyMeSKUMap[deleteProductId];
      } else {
        this.notifyMeSKUMap[deleteProductId] = (
          this.notifyMeSKUMap[deleteProductId] || []
        )?.filter((sku) => sku !== deleteSKU);
      }
      return true;
    } catch (error) {
      error && console.error(error);
      return false;
    } finally {
      this.notifyMeCRUDMeta.setLoading(false);
    }
  }

  /**
   * Subscriptions are keyed by warehouse: the ETA one, virtual LMS warehouses
   * included, so the "subscribed" state and unsubscribe always refer to the
   * warehouse the customer is served from right now.
   */
  private stockWarehouseCode(explicit?: string): string {
    return (
      explicit ??
      orderStore.etaCalculation?.warehouse.code ??
      company.config.warehouse.default
    );
  }

  /**
   * LMS companies list the waitlist across warehouses, so no code is sent; a
   * backend that still demands one gets the ETA warehouse on retry, which keeps
   * the tab alive whichever side is released first.
   */
  private async fetchWaitlist(explicit?: string) {
    const { page, size } = this.notifyMeProductListPagination;
    if (!company.hasNewLogistics) {
      return StockSubscriptionsRequests.getSubscriptions(
        page,
        size,
        this.stockWarehouseCode(explicit),
      );
    }
    try {
      return await StockSubscriptionsRequests.getSubscriptions(page, size);
    } catch (error) {
      if (!isClientError(error)) {
        throw error;
      }
      return StockSubscriptionsRequests.getSubscriptions(
        page,
        size,
        this.stockWarehouseCode(explicit),
      );
    }
  }

  getNotifyMeSKUList = async (warehouseCode?: string): Promise<void> => {
    const requestId = ++this.skuListRequestId;
    this.notifyMeSKUListMeta.setLoading(true);

    try {
      const { data = {} } =
        await StockSubscriptionsRequests.getSubscriptionsSKU(
          this.stockWarehouseCode(warehouseCode),
        );

      if (data && requestId === this.skuListRequestId) {
        runInAction(() => {
          this.notifyMeSKUMap = data;
          this.gotSKUS = true;
        });
      }
    } catch (error) {
      error && console.error(error);
    } finally {
      if (requestId === this.skuListRequestId) {
        this.notifyMeSKUListMeta.setLoading(false);
      }
    }
  };

  getNotifyMeProductsList = async (
    warehouseCode?: string,
    isForce?: boolean,
  ): Promise<boolean> => {
    if (!userStore.isAuthorized) {
      return false;
    }

    try {
      if (isForce) {
        this.notifyMeProductListPagination.page = 1;
        this.notifyMeProductList = new Map<number, Product>();
      } else {
        // Check if all data is already loaded
        const { page, total } = this.notifyMeProductListPagination;
        if (
          total > 0 &&
          (page + 1) * this.notifyMeProductListPagination.size >= total
        ) {
          return false;
        }
        this.notifyMeProductListPagination.page += 1;
      }

      const { data = [], pagination } = await this.fetchWaitlist(warehouseCode);

      runInAction(() => {
        data.forEach((product) => {
          this.notifyMeProductList.set(product.id, new Product(product));

          const skus = (product.offers || [])
            .map((offer) => offer.code)
            .filter(Boolean);
          if (skus.length > 0) {
            this.notifyMeSKUMap[product.id] = skus;
          }
        });

        this.notifyMeProductListPagination.page = pagination.current;
        this.notifyMeProductListPagination.total = pagination.total;
      });

      return data?.length > 0;
    } catch (e) {
      e && console.error(e);
    } finally {
      this.notifyMeSKUListMeta.setLoading(false);
    }

    return false;
  };

  get notifyMeItemsList(): Product[] {
    if (!this.notifyMeProductList.size) {
      return [];
    }
    return Array.from(this.notifyMeProductList, ([, value]) => value);
  }

  reset() {
    this.notifyMeProductList = new Map<number, Product>();
    this.notifyMeSKUMap = {};
    this.gotSKUS = false;
    this.notifyMeProductListPagination.page = 0;
    this.notifyMeProductListPagination.total = 0;
  }

  destroy() {
    this.notifyMeCRUDMeta.destroy();
    this.notifyMeProductListMeta.destroy();
    this.notifyMeSKUListMeta.destroy();
    this._abortController.abort();
  }
}
